import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerDataSource } from 'angular2-smart-table';
import { HttpService } from './common/http.service';
import { getPersonCalculatedFields, Person, PersonConfig, PersonData, } from '../interfaces/person';
import { HttpParams } from '@angular/common/http';
import { omit } from 'lodash';
import { User } from '../interfaces/auth/users';
import { appendFilterParams, defaultDataOmitFields, sanitizeDataForSave } from '../utils/utils.service';

@Injectable()
export class PersonService extends PersonData {
  private readonly url: string;

  constructor(private api: HttpService) {
    super();
    this.url = 'people';

  }

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${this.api.API_BASE_URL}/${this.url}?filters[archivedOn][$null]=true&populate[assignments][populate][]=role&populate[assignments][populate][]=department&populate[appraisals]=true`);
  }

  getCustomGridDataSource(customFilter?: string): ServerDataSource {
    return this.api.getServerDataSource(`${this.api.API_BASE_URL}/${this.url}?filters[archivedOn][$null]=true&populate[assignments][populate][]=role&populate[assignments][populate][]=department&populate[appraisals]=true`+ (customFilter ? `&${customFilter}` : ''));
  }

  list(pageNumber: number, pageSize: number, query: string = '' ): Observable<Person[]> {
    let params: HttpParams = new HttpParams()
    .append('filters[archivedOn][$null]', 'true')
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // If you have filters, sorts, etc. in query, convert them to v5 format
    // Example: ?where[title][$contains]=foo&order=createdAt:desc
    if (query) {
      // You may need to parse and append query params here
      // For now, assume query is already in v5 format
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => {
      return res.data || res;
    }));
  }
  

  listAll(filters?: { [field: string]: any }): Observable<Person[]> {
    // Strapi v4/v5 ignores flat 'limit'/'offset' and falls back to its default
    // page size (usually 25), silently truncating results. Use bracketed
    // offset-pagination params ('pagination[limit]=-1' means "no limit").
    let params: HttpParams = new HttpParams()
      .append('pagination[start]', '0')
      .append('pagination[limit]', '99999')
      .append('populate', '*')
      .append('filters[archivedOn][$null]', 'true');
    params = appendFilterParams(params, filters);

    return this.api.get(this.url, { params }).pipe(map(res => {
      return res.data || res;
    }));
  }

  get(id: any): Observable<Person> {
    return this.api.get(`${this.url}/${id}`, {
      params: new HttpParams()
                              .append('populate[department]', 'true')
                              .append('populate[CV]', 'true')
                              .append('populate[assignments][populate]', '*')
                              .append('populate[user]', 'true')
    }).pipe(map(res => res.data || res));
  }

  create(person: Person | any): Observable<Person> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    
    person = omit(person, [...defaultDataOmitFields, ...getPersonCalculatedFields()]);
    person = sanitizeDataForSave(person, PersonConfig);
    return this.api.post(this.url, {data: person}, { params })
      .pipe(map(res => {
        return res.data;
      }));
  }


  update(Person: Person | any): Observable<Person> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    // Omit read-only/calculated fields but keep full objects for relations so sanitizeDataForSave can extract the correct IDs
    let changedPerson: any = omit(Person, ['id', 'assignments', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy', 'publishedAt', ...getPersonCalculatedFields()]);
    // sanitizeDataForSave will handle extracting numeric id for file fields and documentId for content-type relations
    return this.api.put(`${this.url}/${Person.documentId}`, {data: sanitizeDataForSave(changedPerson, PersonConfig)}, { params })
      .pipe(map(res => {
        return res.data;
      }));
  }

  archive(person: Person, date: Date, user: User): Observable<Person> {
    return this.api.put(`${this.url}/${person.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }
  
  
  unarchive(person: Person): Observable<Person> {
    return this.api.put(`${this.url}/${person.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }


  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }
}
